/**
 * Result object for filtered suggestions and selected index
 */
export interface FilteredSuggestionsResult {
    filteredSuggestions: string[];
    selectedIndex: number;
}
    
/**
 * Filters suggestions based on input and maintains selection state.
 * Handles maintaining selection when list is filtered.
 *
 * @param input - Text after '<>' to filter suggestions
 * @param suggestions - Complete list of available suggestions
 * @param prevFilteredSuggestions - Previously filtered suggestions list
 * @param prevSelectedIndex - Previously selected suggestion index
 * @returns Object containing filtered suggestions and updated selection index
 */
export const filterSuggestions = (
  input: string,
  suggestions: string[],
  prevFilteredSuggestions: string[],
  prevSelectedIndex: number
): FilteredSuggestionsResult => {
  // Filter suggestions that start with input text
  const filtered: string[] = suggestions.filter(s => s.startsWith(input));
  
  // If filtered suggestions are empty, show the user input
  if (filtered.length === 0 && input) {
    return {
      filteredSuggestions: [input],
      selectedIndex: 0
    };
  }


  // Keep current selection if it's still valid, otherwise select first suggestion
  const newSelectedIndex = (() => {
    // If no previous selection, select first item if available
    if (prevSelectedIndex === -1) return filtered.length > 0 ? 0 : -1;
    
    // Try to keep the same suggestion selected after filtering
    const currentSelection: string = prevFilteredSuggestions[prevSelectedIndex];
    const newIndex: number = filtered.findIndex(s => s === currentSelection);
    
    // Return found index, or maintain position if possible, or select first item
    return newIndex !== -1
      ? newIndex
      : (filtered.length > 0 ? (prevSelectedIndex < filtered.length ? prevSelectedIndex : 0) : prevSelectedIndex);
  })();


  return {
    filteredSuggestions: filtered,
    selectedIndex: newSelectedIndex
  };
};